import { useQuery } from '@/hooks/useQuery';
import { useMutation } from '@/hooks/useMutation';
import { recommendedActionsApi } from '@/lib/api/endpoints';
import { toast } from '@/stores/toasts';

export interface RecommendedAction {
  id: number;
  creative_id: number;
  creative_name: string;
  adset_id: number | null;
  campaign_id: number | null;
  kind: 'pause_kill_red';
  status: 'open' | 'dismissed' | 'auto_resolved' | 'manually_resolved';
  reason_snapshot: Record<string, unknown>;
  triggered_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  resolved_by_id: number | null;
}

export interface RecommendedActionsList {
  items: RecommendedAction[];
  total: number;
  page: number;
  page_size: number;
}

export interface RecommendedActionsSummary {
  open_count: number;
  by_kind: Record<string, number>;
  oldest_open_triggered_at: string | null;
}

export interface RecommendedActionFilters {
  status?: 'open' | 'dismissed' | 'auto_resolved' | 'manually_resolved' | null;
  kind?: 'pause_kill_red' | null;
  page?: number;
  pageSize?: number;
}

export function useRecommendedActions(filters: RecommendedActionFilters = {}) {
  const status = filters.status ?? 'open';
  const kind = filters.kind;
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 50;
  const key = `rec-actions:list:${status}:${kind ?? ''}:${page}:${pageSize}`;
  return useQuery<RecommendedActionsList>(key, async () => {
    const { data, error } = await recommendedActionsApi.list({
      status,
      kind,
      page,
      page_size: pageSize,
    });
    if (error) throw new Error('No se pudieron cargar las acciones recomendadas');
    return data as RecommendedActionsList;
  });
}

export function useRecommendedActionsSummary() {
  return useQuery<RecommendedActionsSummary>('rec-actions:summary', async () => {
    const { data, error } = await recommendedActionsApi.summary();
    if (error) throw new Error('No se pudo cargar el resumen de acciones recomendadas');
    return data as RecommendedActionsSummary;
  });
}

export function useDismissRecommendedAction() {
  return useMutation(
    async (id: number) => {
      const { error } = await recommendedActionsApi.dismiss(id);
      if (error) throw new Error('No se pudo descartar la acción recomendada');
    },
    {
      invalidates: ['rec-actions'],
      onSuccess: () => toast.success('Recomendación descartada'),
      onError: (e) => toast.error(e.message),
    },
  );
}

export function useResolveRecommendedAction() {
  return useMutation(
    async (id: number) => {
      const { error } = await recommendedActionsApi.resolve(id);
      if (error) throw new Error('No se pudo marcar como resuelta');
    },
    {
      invalidates: ['rec-actions'],
      onSuccess: () => toast.success('Marcada como resuelta'),
      onError: (e) => toast.error(e.message),
    },
  );
}
